import React, { memo, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import Button from 'components/Button';
import DashboardContainer from 'components/DashboardContainer';
import ProductThumbnail from 'components/ProductThumbnail';
import Pagination from 'components/Pagination';
import Loader from 'components/Loader';

import { productList as TEXT } from 'locales/en';
import PATHS from 'routes/paths';
import {
  getProductList,
  productSelectors,
  editProduct,
} from 'redux/slices/product';
import { logEvent, logCtaWithLabel } from 'utils/ga';

const ProductList = () => {
  const [pageChange, setPageChange] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const products = useSelector(productSelectors.selectAll);
  const productCount = useSelector(productSelectors.selectTotal);
  const nodeRef = useRef(null);

  const pagination = useSelector((state) => state.product.pagination);
  const loading = useSelector((state) => state.product.loading);
  const companyLoading = useSelector((state) => state.company.loading);
  const storeLoading = useSelector((state) => state.javelinStore.loading);
  const displayFullPageLoader =
    storeLoading || companyLoading || (companyLoading && loading);

  const renderView = useCallback(() => {
    const handleChangeOnSale = (productId, value) => {
      dispatch(editProduct({ id: productId, on_sale: value }));
    };

    const onPageChange = async ({ selected }) => {
      window.scrollTo(0, 0);
      setPageChange(true);
      dispatch(getProductList({ page: selected + 1 })).then(() =>
        setPageChange(false)
      );
    };

    if (pageChange) {
      return <Loader />;
    } else {
      return (
        <>
          <CSSTransition
            classNames="transitionGroup"
            in
            appear
            timeout={300}
            key={products[0].id}
            nodeRef={nodeRef}
          >
            <div
              ref={nodeRef}
              className="grid w-full auto-rows-auto auto-cols-auto lg:grid-cols-2 items-start gap-4 mt-9"
            >
              {products.map((product) => {
                return (
                  <ProductThumbnail
                    key={product.id}
                    product={product}
                    onChangeOnSale={() => {
                      logEvent('click', {
                        category: 'live_flag_product',
                        label: product.on_sale
                          ? 'product_inactive'
                          : 'product_active',
                        is_live: product.on_sale ? '0' : '1',
                        product_id: product.id,
                      });
                      handleChangeOnSale(product.id, !product.on_sale);
                    }}
                    onClickClone={(details) =>
                      history.push({
                        pathname: PATHS.productsNew,
                        state: { details },
                      })
                    }
                  />
                );
              })}
            </div>
          </CSSTransition>
          <Pagination
            totalPages={pagination.totalPages}
            currentPage={pagination.currentPage}
            onPageChange={onPageChange}
          />
        </>
      );
    }
  }, [
    dispatch,
    history,
    pageChange,
    pagination.currentPage,
    pagination.totalPages,
    products,
  ]);

  return (
    <DashboardContainer
      header={TEXT.header1}
      subheader={TEXT.header2}
      fullPageLoading={displayFullPageLoader}
      transitionIn={!displayFullPageLoader}
      transitionAppear={displayFullPageLoader}
    >
      <Button
        title={TEXT.create}
        className="btn-primary self-start lg:self-end mt-4 lg:mt-0 py-3 px-2 w-56"
        onClick={() => {
          logCtaWithLabel('create_product');
          history.push(PATHS.productsNew);
        }}
        testId="product-new"
      />
      {!!productCount && renderView()}
    </DashboardContainer>
  );
};

export default memo(ProductList);
